import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage, useT, SUPPORTED_LANGUAGES } from "@/context/LanguageContext";
import { assetUrl } from "@/lib/api";

const scrollToId = (id) => {
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
};

export default function Header({ settings }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLanguage } = useLanguage();
  const t = useT();
  const brand = settings?.brand_name || "Gomes Digital Lab";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV_ITEMS = [
    { label: t("nav.home"), to: "/", section: "home" },
    { label: t("nav.portfolio"), to: "/portfolio", section: "portfolio" },
    { label: t("nav.services"), to: "/", section: "services" },
    { label: t("nav.insights"), to: "/insights", section: "insights" },
    { label: t("nav.about"), to: "/", section: "about" },
    { label: t("nav.contact"), to: "/", section: "contact" },
  ];

  const handleNavClick = (e, item) => {
    const onHome = location.pathname === "/";
    if (item.to === "/portfolio") {
      if (onHome && document.getElementById("portfolio")) {
        e.preventDefault();
        scrollToId("portfolio");
        setOpen(false);
      }
      setOpen(false);
      return;
    }
    if (item.to === "/insights") {
      // Insights is a stand-alone page; never intercept the navigation.
      setOpen(false);
      return;
    }
    if (item.to === "/" && item.section === "home") {
      if (onHome) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setOpen(false);
      return;
    }
    if (item.section) {
      e.preventDefault();
      if (onHome) {
        scrollToId(item.section);
      } else {
        navigate({ pathname: "/", hash: `#${item.section}` });
      }
      setOpen(false);
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      scrollToId("contact");
    } else {
      navigate({ pathname: "/", hash: "#contact" });
    }
    setOpen(false);
  };

  const isActive = (item) => {
    if (item.to === "/portfolio") return location.pathname.startsWith("/portfolio");
    if (item.to === "/insights") return location.pathname.startsWith("/insights");
    if (item.section === "home") return location.pathname === "/" && !location.hash;
    if (item.section) return location.pathname === "/" && location.hash === `#${item.section}`;
    return false;
  };

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-paper/90 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="nav-logo"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          {settings?.logo_url && (
            <img src={assetUrl(settings.logo_url)} alt={brand} fetchpriority="high" decoding="async" className="h-11 w-11 object-contain rounded-full border border-line bg-paper" />
          )}
          <div className="leading-none">
            <div className="font-display text-xl tracking-tight text-ink">
              {brand.split(" ")[0]}{" "}
              <span className="text-graphite font-light italic">{brand.split(" ").slice(1).join(" ")}</span>
            </div>
            <div className="eyebrow-light text-[0.58rem] mt-1">Private Digital Systems</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_ITEMS.map((n) => (
            <Link
              key={n.label + n.section}
              to={n.to}
              data-testid={`nav-${n.section}`}
              onClick={(e) => handleNavClick(e, n)}
              className={`relative text-[0.72rem] uppercase tracking-[0.22em] transition-colors ${
                isActive(n) ? "text-gold-light" : "text-graphite hover:text-ink"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <LanguageSwitcher lang={lang} setLanguage={setLanguage} testidPrefix="lang" />
          <Link
            to="/portal/login"
            className="text-[0.7rem] uppercase tracking-[0.22em] text-graphite hover:text-ink"
            data-testid="header-portal-link"
          >
            {t("nav.clientPortal") || "Client Portal"}
          </Link>
          <Link
            to="/portal/register"
            className="btn-gold-light"
            data-testid="header-cta"
          >
            {t("nav.startProject") || "Start a Project"}
          </Link>
        </div>

        <button
          className="md:hidden text-ink"
          onClick={() => setOpen(!open)}
          aria-label={t("nav.menu")}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-paper border-t border-line" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((n) => (
              <Link
                key={n.label + n.section}
                to={n.to}
                data-testid={`nav-mobile-${n.section}`}
                onClick={(e) => handleNavClick(e, n)}
                className={`text-sm uppercase tracking-[0.2em] ${
                  isActive(n) ? "text-gold-light" : "text-graphite"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <div className="pt-2">
              <LanguageSwitcher lang={lang} setLanguage={setLanguage} testidPrefix="lang-mobile" />
            </div>
            <Link
              to="/portal/login"
              className="text-sm uppercase tracking-[0.2em] text-graphite mt-2"
              data-testid="mobile-portal-link"
              onClick={() => setOpen(false)}
            >
              {t("nav.clientPortal") || "Client Portal"}
            </Link>
            <Link
              to="/portal/register"
              className="btn-gold-light mt-2 self-start"
              data-testid="mobile-header-cta"
              onClick={() => setOpen(false)}
            >
              {t("nav.startProject") || "Start a Project"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function LanguageSwitcher({ lang, setLanguage, testidPrefix }) {
  return (
    <div
      className="flex items-center gap-1 text-[0.7rem] uppercase tracking-[0.22em]"
      data-testid={`${testidPrefix}-switcher`}
      role="group"
      aria-label="Language"
    >
      {SUPPORTED_LANGUAGES.map((code, i) => (
        <span key={code} className="flex items-center">
          <button
            type="button"
            onClick={() => setLanguage(code)}
            data-testid={`${testidPrefix}-${code}`}
            data-active={lang === code ? "true" : "false"}
            className={`px-1.5 py-1 transition-colors ${
              lang === code
                ? "text-gold-light font-medium"
                : "text-graphite hover:text-ink"
            }`}
            aria-pressed={lang === code}
          >
            {code.toUpperCase()}
          </button>
          {i < SUPPORTED_LANGUAGES.length - 1 && (
            <span aria-hidden="true" className="text-line">|</span>
          )}
        </span>
      ))}
    </div>
  );
}
